import { RugbyMatchControl } from 'src/types/matchControl';
import { Clock } from 'src/types/matchControl/clock';
import { RugbyContestant } from 'src/types/matchControl/contestant';
import { Control } from 'src/types/matchControl/control';

import { ShortName, TeamColors } from '../../components';
import ClockDisplay from '../../components/ClockDisplay';
import Period from '../../components/Period';
import styles from '../../MatchControl.module.scss';

import RugbyScoringEvents from './RugbyScoringEvents';

type Props = {
  matchControl: RugbyMatchControl;
  matchControlGraphics: Control[];
  handleDispatchControl: (control?: Control) => void;
  handleStartTime: (clock: Clock) => void;
  handleStopTime: (clock: Clock) => void;
  handleChangeTime: (clock: Clock, time: number) => void;
  handleChangeShortName: (contestant: RugbyContestant, name: string) => void;
  handleChangeColor: (
    contestant: RugbyContestant,
    color: 'primaryColor' | 'secondaryColor',
    value: string
  ) => void;
  handleUpdateMatchControl: (matchControl: RugbyMatchControl) => void;
  handleAddScoringEvent: (contestant: RugbyContestant, eventType: string) => void;
  handleRemoveScoringEvent: (contestant: RugbyContestant, eventType: string) => void;
};

type PeriodProps = {
  matchControl: RugbyMatchControl;
  handleUpdateMatchControl: (matchControl: RugbyMatchControl) => void;
};

type ScoreProps = {
  home: RugbyContestant;
  away: RugbyContestant;
  handleChangeShortName: (contestant: RugbyContestant, name: string) => void;
  handleChangeColor: (
    contestant: RugbyContestant,
    color: 'primaryColor' | 'secondaryColor',
    value: string
  ) => void;
};

type ScoringEventsProps = {
  home: RugbyContestant;
  away: RugbyContestant;
  handleAddScoringEvent: (contestant: RugbyContestant, eventType: string) => void;
  handleRemoveScoringEvent: (contestant: RugbyContestant, eventType: string) => void;
};

const Rugby = ({
  matchControl,
  matchControlGraphics,
  handleChangeColor,
  handleChangeShortName,
  handleChangeTime,
  handleDispatchControl,
  handleStartTime,
  handleStopTime,
  handleUpdateMatchControl,
  handleAddScoringEvent,
  handleRemoveScoringEvent,
}: Props) => {
  const { clocks, contestants } = matchControl;
  const [mainClock] = clocks;
  const [home, away] = contestants;

  return (
    <div className={styles.MainWrapper}>
      <ClockDisplay
        clock={mainClock}
        matchControlGraphics={matchControlGraphics}
        extraTime={mainClock.extraTime}
        showExtraTime={false}
        precision="minutes"
        handleDispatchControl={handleDispatchControl}
        handleStartTime={handleStartTime}
        handleStopTime={handleStopTime}
        handleChangeTime={(time) => handleChangeTime(mainClock, mainClock.clockTime + time)}
      />
      <DividerComponent />
      <ScoreComponent
        home={home}
        away={away}
        handleChangeColor={handleChangeColor}
        handleChangeShortName={handleChangeShortName}
      />
      <DividerComponent />
      <PeriodComponent
        matchControl={matchControl}
        handleUpdateMatchControl={handleUpdateMatchControl}
      />
      <DividerComponent />
      <ScoringEventsSection
        home={home}
        away={away}
        handleAddScoringEvent={handleAddScoringEvent}
        handleRemoveScoringEvent={handleRemoveScoringEvent}
      />
    </div>
  );
};

const ScoreComponent = ({ home, away, handleChangeShortName, handleChangeColor }: ScoreProps) => (
  <div className={styles.ItemWrapper}>
    <div className={styles.CurrentScoreWrapper}>
      <div className="flex flex-col justify-between items-center gap-2">
        <ShortName
          shortName={home.shortName}
          handleChangeShortName={(name) => handleChangeShortName(home, name)}
        />
        <TeamColors
          primaryColor={home.primaryColor}
          secondaryColor={home.secondaryColor}
          handleChangePrimaryColor={(color) => handleChangeColor(home, 'primaryColor', color)}
          handleChangeSecondaryColor={(color) => handleChangeColor(home, 'secondaryColor', color)}
        />
      </div>
      <div className={styles.ScoreWrapper}>
        <div className={styles.FlagWrapper + ' ' + styles.HomeFlag}>
          <div className={styles.ScoreFlagText + ' text-white text-4xl font-bold'}>
            {home.score.points}
          </div>
        </div>
      </div>
      <div className={styles.ScoreWrapper}>
        <div className={styles.FlagWrapper + ' ' + styles.GuestsFlag}>
          <div className={styles.ScoreFlagText + ' text-white text-4xl font-bold'}>
            {away.score.points}
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-between items-center gap-2">
        <ShortName
          inverted
          shortName={away.shortName}
          handleChangeShortName={(name) => handleChangeShortName(away, name)}
        />
        <TeamColors
          primaryColor={away.primaryColor}
          secondaryColor={away.secondaryColor}
          handleChangePrimaryColor={(color) => handleChangeColor(away, 'primaryColor', color)}
          handleChangeSecondaryColor={(color) => handleChangeColor(away, 'secondaryColor', color)}
        />
      </div>
    </div>
  </div>
);

const PeriodComponent = ({ matchControl, handleUpdateMatchControl }: PeriodProps) => (
  <div className="flex items-center gap-2">
    <div className="flex-auto min-w-0 font-semibold">Half</div>
    <div>
      <Period
        period={matchControl.period}
        handleAddPeriod={() =>
          handleUpdateMatchControl({
            ...matchControl,
            period: Math.min(2, matchControl.period + 1),
          })
        }
        handleRemovePeriod={() =>
          handleUpdateMatchControl({
            ...matchControl,
            period: Math.max(1, matchControl.period - 1),
          })
        }
      />
    </div>
  </div>
);

const ScoringEventsSection = ({
  home,
  away,
  handleAddScoringEvent,
  handleRemoveScoringEvent,
}: ScoringEventsProps) => (
  <div className={styles.ItemWrapper}>
    <div className="flex flex-row gap-6 w-full">
      <div className="flex flex-col gap-4 flex-1 min-w-0">
        <div className="text-sm font-semibold text-center">{home.shortName}</div>
        <RugbyScoringEvents
          contestant={home}
          onAddTry={() => handleAddScoringEvent(home, 'try')}
          onRemoveTry={() => handleRemoveScoringEvent(home, 'try')}
          onAddConversion={() => handleAddScoringEvent(home, 'conversion')}
          onRemoveConversion={() => handleRemoveScoringEvent(home, 'conversion')}
          onAddPenaltyKick={() => handleAddScoringEvent(home, 'penalty_kick')}
          onRemovePenaltyKick={() => handleRemoveScoringEvent(home, 'penalty_kick')}
          onAddDropGoal={() => handleAddScoringEvent(home, 'drop_goal')}
          onRemoveDropGoal={() => handleRemoveScoringEvent(home, 'drop_goal')}
        />
      </div>
      <div className="border-r border-elevated-2" />
      <div className="flex flex-col gap-4 flex-1 min-w-0">
        <div className="text-sm font-semibold text-center">{away.shortName}</div>
        <RugbyScoringEvents
          contestant={away}
          onAddTry={() => handleAddScoringEvent(away, 'try')}
          onRemoveTry={() => handleRemoveScoringEvent(away, 'try')}
          onAddConversion={() => handleAddScoringEvent(away, 'conversion')}
          onRemoveConversion={() => handleRemoveScoringEvent(away, 'conversion')}
          onAddPenaltyKick={() => handleAddScoringEvent(away, 'penalty_kick')}
          onRemovePenaltyKick={() => handleRemoveScoringEvent(away, 'penalty_kick')}
          onAddDropGoal={() => handleAddScoringEvent(away, 'drop_goal')}
          onRemoveDropGoal={() => handleRemoveScoringEvent(away, 'drop_goal')}
        />
      </div>
    </div>
  </div>
);

const DividerComponent = () => <div className={styles.Divider} />;

export default Rugby;
